/**
 * مصدر واحد لاختيار نصوص منتج Shopify بحسب اللغة.
 * الأولويّة: قاعدتنا (product_translations) → metafield Shopify بلغة المستخدم →
 * metafield بلغة أخرى → قيمة Shopify الافتراضيّة.
 *
 * metafields: descriptions.ar / descriptions.en / descriptions.title_ar / descriptions.title_en
 */
import type { ShopifyProductNode } from "@/lib/shopify";

type MetaLike = { value: string } | null | undefined;

export type LocalTranslation = {
  titleAr: string;
  titleEn: string;
  descriptionAr: string;
  descriptionEn: string;
  /** نوتات العطر من قاعدتنا — تُستخدم عند غياب metafields Shopify. */
  notesTopAr?: string;
  notesTopEn?: string;
  notesMiddleAr?: string;
  notesMiddleEn?: string;
  notesBaseAr?: string;
  notesBaseEn?: string;
};

export type TranslationLookup =
  | Record<string, LocalTranslation | undefined>
  | null
  | undefined;

function pick(
  localAr: string,
  localEn: string,
  ar: MetaLike,
  en: MetaLike,
  locale: string,
  fallback: string,
): string {
  const a = (ar?.value ?? "").trim();
  const e = (en?.value ?? "").trim();
  if (locale.startsWith("ar")) return localAr.trim() || a || localEn.trim() || e || fallback;
  return localEn.trim() || e || localAr.trim() || a || fallback;
}

type TitleLike =
  | (Pick<ShopifyProductNode, "title" | "titleAr" | "titleEn"> & { handle?: string })
  | null
  | undefined;

type DescLike =
  | (Pick<ShopifyProductNode, "description" | "descriptionAr" | "descriptionEn"> & {
      handle?: string;
    })
  | null
  | undefined;

export function localizedTitle(
  product: TitleLike,
  locale: string,
  lookup?: TranslationLookup,
): string {
  if (!product) return "";
  const local = (product.handle && lookup?.[product.handle]) || undefined;
  return pick(
    local?.titleAr ?? "",
    local?.titleEn ?? "",
    product.titleAr,
    product.titleEn,
    locale,
    product.title ?? "",
  );
}

export function localizedDescription(
  product: DescLike,
  locale: string,
  lookup?: TranslationLookup,
): string {
  if (!product) return "";
  const local = (product.handle && lookup?.[product.handle]) || undefined;
  return pick(
    local?.descriptionAr ?? "",
    local?.descriptionEn ?? "",
    product.descriptionAr,
    product.descriptionEn,
    locale,
    product.description ?? "",
  );
}

/**
 * نوتات العطر من قاعدتنا كنصّ واحد لكلّ handle — تُستخدم كمصدر
 * العائلة العطريّة في التصنيف/البحث بدل الوصف التسويقيّ العامّ.
 */
export function notesByHandle(lookup?: TranslationLookup): Record<string, string> {
  const out: Record<string, string> = {};
  if (!lookup) return out;
  for (const [handle, t] of Object.entries(lookup)) {
    if (!t) continue;
    const text = [
      t.notesTopEn, t.notesTopAr,
      t.notesMiddleEn, t.notesMiddleAr,
      t.notesBaseEn, t.notesBaseAr,
    ].filter(Boolean).join(", ").trim();
    if (text) out[handle] = text;
  }
  return out;
}
